// Vendor Imports
import { Component } from 'react';

// Project Imports
import { I18n } from 'common/i18n';
import {
  doAnyFiltersHaveResettableChanges,
  resetAllFiltersToDefaults,
} from 'common/components/FilterBar/lib/allFilters';
import { type SoqlFilter } from './SoqlFilter';
import { type FilterBarColumns } from './index';
import { type FilterBarColumn, type FilterBarPendoIds } from './types';
import { type DataSourceCCVs, type FilterParameterConfiguration } from 'common/types/reportFilters';
import { ForgeButton } from '@tylertech/forge-react';

// Constants
const scope = 'shared.components.filter_bar';

export interface ResetFiltersButtonProps {
  clientContextVariables: DataSourceCCVs | undefined;
  columns: FilterBarColumns;
  computedColumns?: FilterBarColumn[];
  editMode: boolean;
  filterParameterConfigurations?: FilterParameterConfiguration[];
  isReadOnly?: boolean;
  onReset: (updated: SoqlFilter[]) => void;
  onResetAll: (updated: FilterParameterConfiguration[], clearAll?: boolean) => void;
  pendoIds?: FilterBarPendoIds;
}

export class ResetFiltersButton extends Component<ResetFiltersButtonProps> {
  onKeyDown = (event: React.KeyboardEvent) => {
    if (event.code === 'Enter' || event.code === 'Space') {
      event.preventDefault();
      this.onClick();
    }
  };

  onClick = () => {
    const {
      clientContextVariables,
      columns,
      computedColumns = [],
      editMode,
      filterParameterConfigurations,
      isReadOnly,
      onReset,
      onResetAll,
    } = this.props;
    if (filterParameterConfigurations) {
      const resetFilters = resetAllFiltersToDefaults(
        filterParameterConfigurations,
        columns,
        clientContextVariables,
        editMode,
        isReadOnly,
        computedColumns,
      );
      onResetAll(resetFilters, true);

      // This is needed for visualization & measures filters to be reset
      // with the onUpdate callback
      onReset(
        resetFilters.map((filter) => {
          return filter.config as SoqlFilter;
        }),
      );
    }
  };

  isDisabled = () => {
    const { clientContextVariables, editMode, filterParameterConfigurations, isReadOnly } = this.props;

    return !doAnyFiltersHaveResettableChanges(
      filterParameterConfigurations || [],
      editMode,
      clientContextVariables,
      isReadOnly,
    );
  };

  render() {
    const { pendoIds } = this.props;
    return (
      <div
        className="reset-filters"
        id={pendoIds?.resetAllFilters}
        data-testid="filter-bar-reset-all-filters"
      >
        <ForgeButton
          className="btn-reset-filters"
          disabled={this.isDisabled()}
          onClick={this.onClick}
          aria-label={I18n.t('clear_all_filters', { scope })}
          onKeyDown={this.onKeyDown}
          tabIndex={0}
          role="button"
        >
          {I18n.t('clear_all_filters', { scope })}
        </ForgeButton>
      </div>
    );
  }
}

export default ResetFiltersButton;
